<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';
import { isGifMessage } from 'dashboard/helper/gifMediaHelper';
import {
  isStickerMessage,
  STICKER_LABEL,
} from 'dashboard/helper/stickerMessageHelper';
import {
  groupParticipantName,
  isGroupMessage,
  stripGroupSenderPrefix,
} from 'dashboard/components-next/message/groupParticipant.js';

export default {
  name: 'MessagePreview',
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  computed: {
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      if (subject) return this.getPlainText(subject);

      // O autor saiu do corpo da mensagem e passou a ser metadado. Aqui ele é
      // recolocado na frente do texto de propósito: numa lista de conversas,
      // "Bruno: bom dia" é o que o WhatsApp mostra e o que distingue um grupo
      // ativo de uma conversa individual. A diferença é que agora vem de
      // content_attributes — o texto em si permanece limpo.
      const content = stripGroupSenderPrefix(
        this.message.content,
        contentAttributes
      );
      const plain = this.getPlainText(content);

      if (this.messageByAgent || !isGroupMessage(contentAttributes)) {
        return plain;
      }

      const participant = groupParticipantName(contentAttributes);
      return participant ? `${participant}: ${plain}` : plain;
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments;
      // Aceleres 2026-08-12: o GIF que o WhatsApp manda e um MP4 mudo, com
      // `file_type: 'video'`, entao a previa da lista dizia "Mensagem de video"
      // para um GIF. O tipo de EXIBICAO nao esta no anexo, esta na mensagem.
      if (isGifMessage(this.message)) return 'gif';
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      return isStickerMessage(this.message);
    },
    stickerLabel() {
      return STICKER_LABEL;
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="lock-closed"
      />
      <!-- Aceleres 2026-09-02: a seta de resposta (`arrow-reply`) que marcava a
           ultima mensagem como enviada pelo painel saiu da previa do cartao, a
           pedido. Quem responde por "de quem foi a ultima mensagem" na lista
           passa a ser so o texto; o cadeado da nota privada e o (i) da
           atividade ficam, porque dizem outra coisa. `messageByAgent`
           continua a servir a `parsedLastMessage` (o nome do participante do
           grupo nao se poe a frente do que nos proprios enviamos). -->
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="info"
      />
    </template>
    <span v-if="isMessageSticker">
      <i
        v-if="showMessageType"
        class="i-ph-smiley-sticker -mt-0.5 size-4 align-middle inline-block text-n-slate-11"
        aria-hidden="true"
      />
      {{ stickerLabel }}
    </span>
    <span v-else-if="message.content">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments">
      <fluent-icon
        v-if="attachmentIcon && showMessageType"
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        :icon="attachmentIcon"
      />
      {{ $t(`${attachmentMessageContent}`) }}
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
